import {
  ASSET_RECOVERY_KEY,
  isLocalDevelopmentHost,
  recoveryDecision,
} from './assetPolicy';

export function markAssetRecoveryHealthy(storage) {
  try {
    storage.removeItem(ASSET_RECOVERY_KEY);
  } catch {
    /* session storage unavailable */
  }
}

export function showAssetRecoveryFallback(doc = document) {
  if (!doc || doc.getElementById('asset-recovery-fallback')) return;

  const root = doc.getElementById('root') || doc.body;
  if (!root) return;

  const shell = doc.createElement('div');
  shell.id = 'asset-recovery-fallback';
  shell.setAttribute('role', 'alert');
  shell.style.cssText = [
    'min-height:100vh',
    'box-sizing:border-box',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'padding:32px 20px',
    'background:#080809',
    'color:#f4f1ea',
    'font-family:Georgia, "Times New Roman", serif',
    'text-align:center',
  ].join(';');

  const panel = doc.createElement('div');
  panel.style.cssText = 'max-width:460px';

  const title = doc.createElement('h1');
  title.textContent = 'This page could not finish loading';
  title.style.cssText = 'font-size:32px;font-weight:500;margin:0 0 12px';

  const message = doc.createElement('p');
  message.textContent =
    'A cached copy of the site pointed at files that are no longer available. Saved preferences were left untouched.';
  message.style.cssText = 'font-family:system-ui,sans-serif;font-size:16px;line-height:1.5;color:#d5d0c8;margin:0 0 24px';

  const button = doc.createElement('button');
  button.id = 'asset-recovery-reload';
  button.type = 'button';
  button.textContent = 'Reload';
  button.style.cssText = [
    'appearance:none',
    'border:0',
    'border-radius:999px',
    'background:#e8a849',
    'color:#1a1408',
    'font-family:system-ui,sans-serif',
    'font-size:15px',
    'font-weight:650',
    'padding:12px 22px',
    'cursor:pointer',
  ].join(';');
  button.addEventListener('click', () => {
    try {
      doc.defaultView.sessionStorage.removeItem(ASSET_RECOVERY_KEY);
    } catch {
      /* ignore */
    }
    doc.defaultView.location.reload();
  });

  panel.append(title, message, button);
  shell.append(panel);
  root.replaceChildren(shell);
  if (doc.body) doc.body.style.background = '#080809';
}

/**
 * One reload after deleting portfolio caches. Does not clear localStorage.
 * A second failure in the same tab shows the fallback instead of looping.
 */
export async function recoverFromStaleAssets({
  sessionStorage,
  caches,
  getRegistrations,
  reload,
  showFallback,
  hostname = '',
}) {
  if (isLocalDevelopmentHost(hostname)) return;

  let decision = 'fallback';
  try {
    decision = recoveryDecision(sessionStorage.getItem(ASSET_RECOVERY_KEY));
  } catch {
    showFallback();
    return;
  }

  if (decision === 'wait') return;
  if (decision === 'fallback') {
    showFallback();
    return;
  }

  try {
    sessionStorage.setItem(ASSET_RECOVERY_KEY, 'pending');
  } catch {
    showFallback();
    return;
  }

  try {
    if (caches && caches.keys) {
      const names = await caches.keys();
      await Promise.all(
        names
          .filter((name) => name.startsWith('portfolio-'))
          .map((name) => caches.delete(name))
      );
    }
    if (getRegistrations) {
      const registrations = await getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {
    /* reload with whatever could be cleared */
  }

  try {
    sessionStorage.setItem(ASSET_RECOVERY_KEY, 'attempted');
  } catch {
    /* the reload still has to happen */
  }
  reload();
}
